// pothos «ext-collector» — бэкенд Chrome-расширения Collector (Google Images →
// R2 `pothos-chrome-extension`). Бриф 2026-08-23.
//
// Клиент — расширение (plain JS, zod не тянет), поэтому схемы здесь прежде
// всего фиксируют форму ответов воркера (parseRes на маршрутах) и тела
// админ-ручек CLI. Авторизация сотрудника: `Authorization: Bearer <token>`,
// токен выдаётся через /api/_ext/tokens (X-Intake-Token).
import { z } from 'zod';
import { AssetCategorySchema } from '../vocab/catalog';

export const EXT_EVENT_STATUS = ['uploaded', 'url_only'] as const;
export const ExtEventStatusSchema = z.enum(EXT_EVENT_STATUS);

export const ExtDeckCategorySchema = z.union([AssetCategorySchema, z.literal('')]);

// ── сотрудник (Bearer) ──────────────────────────────────────────────────────

export const PothosExtHealthResSchema = z.object({
  ok: z.boolean(),
  label: z.string(),
  email: z.string(),
  decks_version: z.string(),
});
export type PothosExtHealthRes = z.infer<typeof PothosExtHealthResSchema>;

export const PothosExtDeckSchema = z.object({
  dir: z.string(),
  name: z.string(),
  category: ExtDeckCategorySchema,
  locale_code: z.string(),
  list: z.array(z.string()),
});
export type PothosExtDeck = z.infer<typeof PothosExtDeckSchema>;

export const PothosExtDecksResSchema = z.object({
  version: z.string(),
  decks: z.array(PothosExtDeckSchema),
});
export type PothosExtDecksRes = z.infer<typeof PothosExtDecksResSchema>;

export const PothosExtDecksVersionResSchema = z.object({ version: z.string() });

/** Поля multipart-тела POST /api/ext/assets (все строки; file — blob только при uploaded). */
export const PothosExtAssetMetaSchema = z.object({
  img_url: z.string().min(1).max(4096),
  source_page: z.string().max(4096).default(''),
  keyword: z.string().max(500).default(''),
  deck: z.string().max(120).default(''),
  status: ExtEventStatusSchema,
  via: z.string().max(40).default(''),
  client_id: z.string().max(40).default(''),
  captured_at: z.string().max(40).default(''),
  user_agent: z.string().max(400).default(''),
  filename: z.string().max(200).default(''),
  keyword_id: z.string().max(40).default(''),
});
export type PothosExtAssetMeta = z.infer<typeof PothosExtAssetMetaSchema>;

export const PothosExtAssetResSchema = z.object({
  ok: z.boolean(),
  event_id: z.string(),
  status: ExtEventStatusSchema,
  duplicate: z.boolean(),
  r2_key: z.string(),
  reason: z.string(),
});
export type PothosExtAssetRes = z.infer<typeof PothosExtAssetResSchema>;

export const PothosExtStatsResSchema = z.object({
  ok: z.boolean(),
  label: z.string(),
  today: z.object({ clicked: z.number(), uploaded: z.number(), url_only: z.number() }),
  total: z.object({ clicked: z.number(), uploaded: z.number(), url_only: z.number() }),
});
export type PothosExtStatsRes = z.infer<typeof PothosExtStatsResSchema>;

// ── админ (X-Intake-Token, CLI) ─────────────────────────────────────────────

export const PothosExtTokensReqSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('create'),
    label: z.string().min(1).max(40).regex(/^[a-z0-9_-]+$/i, 'label: латиница/цифры/_-'),
    email: z.string().max(200).default(''),
    note: z.string().max(400).default(''),
  }),
  z.object({ op: z.literal('revoke'), label: z.string().min(1) }),
  z.object({ op: z.literal('list') }),
]);
export type PothosExtTokensReq = z.infer<typeof PothosExtTokensReqSchema>;

export const PothosExtTokenRowSchema = z.object({
  label: z.string(), email: z.string(), note: z.string(), active: z.boolean(),
  created_at: z.string(), last_seen_at: z.string(), last_client_id: z.string(),
  events: z.number(),
});
export const PothosExtTokensResSchema = z.object({
  ok: z.boolean(),
  token: z.string(),            // только в ответе на create — показывается один раз
  tokens: z.array(PothosExtTokenRowSchema),
  reason: z.string(),
});
export type PothosExtTokensRes = z.infer<typeof PothosExtTokensResSchema>;

export const PothosExtDecksUpsertReqSchema = z.object({
  decks: z.array(z.object({
    dir: z.string().min(1).max(120).regex(/^[a-z0-9_-]+$/i),
    name: z.string().min(1).max(200),
    category: ExtDeckCategorySchema.default(''),
    locale_code: z.string().max(10).default(''),
    list: z.array(z.string().min(1).max(500)).min(1),
    position: z.number().int().default(0),
    active: z.boolean().default(true),
  })).min(1).max(200),
  /** true — деки, которых нет в запросе, деактивируются. */
  replace: z.boolean().default(false),
});
export type PothosExtDecksUpsertReq = z.infer<typeof PothosExtDecksUpsertReqSchema>;

export const PothosExtDecksUpsertResSchema = z.object({
  ok: z.boolean(), upserted: z.number(), deactivated: z.number(), version: z.string(),
});

export const PothosExtEventRowSchema = z.object({
  event_id: z.string(), label: z.string(), client_id: z.string(),
  img_url: z.string(), source_page: z.string(), keyword: z.string(), deck: z.string(),
  category: z.string(), locale_code: z.string(), status: ExtEventStatusSchema, via: z.string(),
  bytes: z.number(), sha256: z.string(), r2_key: z.string(), duplicate_of: z.string(),
  captured_at: z.string(), received_at: z.string(),
});
export const PothosExtEventsResSchema = z.object({
  ok: z.boolean(),
  events: z.array(PothosExtEventRowSchema),
  next_before: z.string(),      // received_at последней строки — курсор для следующей страницы
});
export type PothosExtEventsRes = z.infer<typeof PothosExtEventsResSchema>;

// ── релизы расширения (zip в R2, самообновление «скачать → распаковать → перезапустить») ──

export const PothosExtReleaseResSchema = z.object({
  ok: z.boolean(),
  version: z.string(),          // '' — релизов нет
  notes: z.string(),
  size: z.number(),
  published_at: z.string(),
  zip_path: z.string(),         // путь для скачивания (Bearer) — /api/ext/releaseZip
});
export type PothosExtReleaseRes = z.infer<typeof PothosExtReleaseResSchema>;

/** multipart POST /api/_ext/release: version, notes + file (zip). */
export const PothosExtReleasePushMetaSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'version: x.y.z'),
  notes: z.string().max(2000).default(''),
});

// ── конвейер ключевиков (мигр. 0107; KEYWORD_PIPELINE.md) ───────────────────

export const EXT_KEYWORD_STATUS = ['candidate', 'active', 'paused', 'dead'] as const;
export const ExtKeywordStatusSchema = z.enum(EXT_KEYWORD_STATUS);
export const EXT_KEYWORD_ORIGIN = ['seed', 'entity_mining', 'geo_expansion', 'source_scoped', 'genre_split', 'agent', 'manual'] as const;
export const ExtKeywordOriginSchema = z.enum(EXT_KEYWORD_ORIGIN);
export const ExtModalitySchema = z.enum(['photo', 'screenshot', '']);
export const EXT_REJECT_REASON = [
  'app_screenshot', 'receipt', 'signage', 'product_packaging', 'book_label', 'wrong_locale', 'wrong_category',
  // 'pii' — УСТАРЕЛА (Глеб, 2026-08-31: PII не является причиной отказа).
  // Значение оставлено в словаре: им размечены уже накопленные строки.
  'too_small', 'no_code', 'junk_code', 'duplicate', 'pii', 'render_mockup',
  // Гейт декодера 2026-08-31 (packages/shared/decode/classify.ts, codeGate):
  // класс кадра виден в статистике источников, а не сваливается в junk_code.
  'payment_qr',        // платёжный QR: Zelle, Alipay, WeChat Pay, UPI, PIX, EMVCo
  'transport_ticket',  // посадочный талон (IATA BCBP) / бинарный транзитный код
  'app_link',          // единственный код — ссылка «скачай приложение»
  'promo_banner',      // рекламная плашка акции или форма-опросник, а не ассет
  'cropped_fragment',  // фрагмент карты крупным планом, границ в кадре нет (vision)
  'blocked_domain',    // донор с заблокированного домена (.ru/.su/.рф)
  'bad_file_type',     // формат, которого у реальных карт не бывает (GIF)
  // на ассете только английский, целевого языка локали нет — НЕ годен (Глеб,
  // 2026-08-23); считается отдельно по локали и домену, чтобы «много
  // положительных, но английских» не выглядело как хороший источник
  'english_only',
  // целевого языка на ассете нет вообще, но и не «чисто английский» случай
  // (Глеб, 2026-08-31: «совсем без целевого языка — reject»); ставит серверный
  // языковой гейт eval'а (applyLanguageGate в ext-eval.ts)
  'no_target_language',
  // мерчанта не опознал ни визуал, ни fallback по домену страницы-источника —
  // кадр не должен тратить время QA (Глеб, 2026-08-31); ставит гейт мерчанта
  // на входе в intake (intakeAccepted в ext-eval.ts)
  'no_merchant',
  'other',
] as const;
export const ExtRejectReasonSchema = z.enum(EXT_REJECT_REASON);

/** Элемент очереди сотрудника (GET /api/ext/queue). */
export const PothosExtQueueItemSchema = z.object({
  keyword_id: z.string(),
  text: z.string(),
  locale_code: z.string(),
  category: z.string(),
  modality: z.string(),
  deck: z.string(),
  room: z.number(),           // remaining по ячейке плана на момент выдачи
});
export const PothosExtQueueResSchema = z.object({
  ok: z.boolean(),
  queue_id: z.string(),
  items: z.array(PothosExtQueueItemSchema),
  version: z.string(),        // MAX(updated_at) по ключевикам — для подсветки «есть новые»
  cells: z.number(),          // сколько ячеек плана участвовало
});
export type PothosExtQueueRes = z.infer<typeof PothosExtQueueResSchema>;

export const EXT_CELL_STAGE = ['generic', 'niche', 'research'] as const;
export const ExtCellStageSchema = z.enum(EXT_CELL_STAGE);

export const PothosExtPlanCellSchema = z.object({
  locale_code: z.string(), language: z.string(), country: z.string(), priority: z.string(),
  category: z.string(), modality: z.string(),
  hard_limit: z.number(), total: z.number(), remaining: z.number(),
  keywords_active: z.number(), keywords_candidate: z.number(),
  clicked: z.number(), accepted: z.number(),
  stage: ExtCellStageSchema,          // стратегия ячейки: generic → niche → research
  tested: z.number(),                 // проверенных ключевиков текущей стадии
  useful: z.number(),                 // полезных находок (без english_only и закапанных мерчантов)
});
export const PothosExtCellStageReqSchema = z.object({
  agent: z.string().min(1).max(60),
  cells: z.array(z.object({
    locale_code: z.string().min(2), category: AssetCategorySchema, modality: z.enum(['photo', 'screenshot']),
    stage: ExtCellStageSchema, locked: z.boolean().default(true), note: z.string().max(300).default(''),
  })).min(1).max(300),
});
export const PothosExtPlanResSchema = z.object({ ok: z.boolean(), cells: z.array(PothosExtPlanCellSchema), generated_at: z.string() });
export type PothosExtPlanRes = z.infer<typeof PothosExtPlanResSchema>;

export const PothosExtKeywordRowSchema = z.object({
  keyword_id: z.string(), text: z.string(), locale_code: z.string(), category: z.string(), modality: z.string(),
  deck: z.string(), origin: z.string(), parent_id: z.string(), merchant: z.string(), status: ExtKeywordStatusSchema,
  probe_quota: z.number(), served: z.number(), created_by: z.string(), note: z.string(),
  created_at: z.string(), last_served_at: z.string(),
  clicked: z.number(), uploaded: z.number(), url_only: z.number(), evaluated: z.number(),
  accepted: z.number(), rejected: z.number(), unique_merchants: z.number(), unique_domains: z.number(),
  noise_top: z.string(), yield: z.number(), diversity: z.number(), english_only: z.number(),
});
export const PothosExtKeywordsResSchema = z.object({ ok: z.boolean(), keywords: z.array(PothosExtKeywordRowSchema) });

export const PothosExtSourceRowSchema = z.object({
  locale_code: z.string(), category: z.string(), domain: z.string(),
  assets_total: z.number(), uploaded: z.number(), evaluated: z.number(), accepted: z.number(),
  english_only: z.number(),   // отклонено как «только английский» — источник кормит не тем языком
  capped_merchant: z.number(), // принято, но мерчант уже в капе (≤20/локаль×категория) — за результат не считается
  accept_rate: z.number(), share_of_accepted: z.number(), scraper_candidate: z.boolean(),
});
export const PothosExtSourcesResSchema = z.object({ ok: z.boolean(), sources: z.array(PothosExtSourceRowSchema) });

export const PothosExtProposeReqSchema = z.object({
  agent: z.string().min(1).max(60),
  note: z.string().max(1000).default(''),
  keywords: z.array(z.object({
    text: z.string().min(1).max(300),
    locale_code: z.string().min(2).max(10),
    category: AssetCategorySchema,
    modality: ExtModalitySchema.default(''),
    deck: z.string().max(120).default(''),
    origin: ExtKeywordOriginSchema.default('agent'),
    parent_id: z.string().max(40).default(''),
    probe_quota: z.number().int().min(1).max(200).default(20),
    merchant: z.string().max(120).default(''),   // целевой мерчант (research) — сервер отбросит, если он в капе
    note: z.string().max(300).default(''),
  })).min(1).max(2000),
});
export type PothosExtProposeReq = z.infer<typeof PothosExtProposeReqSchema>;
export const PothosExtProposeResSchema = z.object({
  ok: z.boolean(), run_id: z.string(), proposed: z.number(), inserted: z.number(), duplicates: z.number(),
  capped_rejected: z.array(z.string()),   // тексты ключевиков, отброшенных из-за мерчанта в капе
});

export const PothosExtKeywordStatusReqSchema = z.object({
  agent: z.string().min(1).max(60),
  changes: z.array(z.object({ keyword_id: z.string().min(1), status: ExtKeywordStatusSchema, note: z.string().max(300).default('') })).min(1).max(2000),
});
export const PothosExtEvalReqSchema = z.object({
  by: z.string().min(1).max(60),                       // auto:<engine> | human:<label>
  evals: z.array(z.object({
    event_id: z.string().min(1),
    verdict: z.enum(['accepted', 'rejected']),
    reject_reason: z.union([ExtRejectReasonSchema, z.literal('')]).default(''),
    merchant: z.string().max(120).default(''),
    modality: ExtModalitySchema.default(''),
    // приложение/платформа, внутри которой сделан скриншот (не сайт мерчанта) — шаг «агрегатор?»
    aggregator: z.string().max(120).default(''),
    // языки видимого текста от визуала (ISO-коды, как отдала модель; сервер
    // нормализует) — кормят языковой гейт целевого языка и secondary_locale
    languages: z.array(z.string().min(1).max(8)).max(16).default([]),
    // категория по мнению визуала (вокабуляр каталога); расходится с ячейкой →
    // маркер доразметки 'category?:' в check_comment ассета
    category: z.union([AssetCategorySchema, z.literal('')]).default(''),
  })).min(1).max(2000),
});
export const PothosExtSimpleResSchema = z.object({ ok: z.boolean(), changed: z.number() });
export const PothosExtEvalResSchema = z.object({
  ok: z.boolean(),
  changed: z.number(),
  // сколько «accepted» перевернул серверный языковой гейт
  // (english_only / no_target_language)
  flipped: z.number(),
});

// ─── этап 2: авто-приёмка (раннер на Mac mini, X-Agent-Secret) ──────────────
// Порядок: pending(stage=codes) → bytes → zxing на mini → codes (воркер:
// классификация + гейт дублей, ставит no_code/junk_code/product_packaging/
// duplicate сам) → pending(stage=vision) → qwen на mini → eval → intake.

export const EXT_EVAL_STAGE = ['codes', 'vision'] as const;
export const ExtEvalStageSchema = z.enum(EXT_EVAL_STAGE);

export const PothosExtPendingItemSchema = z.object({
  event_id: z.string(),
  r2_key: z.string(),
  content_type: z.string(),
  bytes: z.number(),
  label: z.string(),
  keyword: z.string(),
  locale_code: z.string(),
  language: z.string(),       // язык локали (PACK_LOCALES) — для english_only
  category: z.string(),
  modality: z.string(),
  source_page: z.string(),
  img_url: z.string(),
  codes: z.array(z.object({ symbology: z.string(), value: z.string(), is_primary: z.boolean() })).default([]),
});
export type PothosExtPendingItem = z.infer<typeof PothosExtPendingItemSchema>;
export const PothosExtPendingResSchema = z.object({
  ok: z.boolean(),
  stage: ExtEvalStageSchema,
  items: z.array(PothosExtPendingItemSchema),
  remaining: z.number().int(),
});

export const PothosExtCodesReqSchema = z.object({
  by: z.string().min(1).max(60),                      // auto:zxing-cpp@mini
  items: z.array(z.object({
    event_id: z.string().min(1),
    codes: z.array(z.object({
      symbology: z.string().max(32).default(''),
      value: z.string().min(1).max(4096),
    })).max(40).default([]),
    // Размер кадра с декодера: гейт «короткая сторона < 300 px» считается на
    // воркере ДО визуала (Глеб, 2026-08-31). 0 = декодер не сообщил, не гейтим.
    width: z.number().int().min(0).max(100000).default(0),
    height: z.number().int().min(0).max(100000).default(0),
    decode_error: z.string().max(200).default(''),      // декодер упал — событие остаётся в очереди
  })).min(1).max(500),
});
export const PothosExtCodesResSchema = z.object({
  ok: z.boolean(),
  results: z.array(z.object({
    event_id: z.string(),
    next: z.enum(['vision', 'rejected', 'retry']),
    reject_reason: z.union([ExtRejectReasonSchema, z.literal('')]).default(''),
    duplicate_of: z.string().default(''),               // asset_id или event_id владельца кода
  })),
});

export const PothosExtIntakeReqSchema = z.object({
  limit: z.number().int().min(1).max(60).default(30),
});
export const PothosExtIntakeResSchema = z.object({
  ok: z.boolean(),
  copied: z.number().int(),
  processed: z.number().int(),
  states: z.record(z.string(), z.number().int()),
  batches: z.array(z.string()),
});

// ─── шаг «агрегатор?»: кандидаты по принятым скриншотам (мост в research на mini) ─
export const EXT_AGG_STATUS = ['candidate', 'confirmed', 'rejected', 'bridged', 'error'] as const;
export const ExtAggStatusSchema = z.enum(EXT_AGG_STATUS);
export const PothosExtAggregatorSchema = z.object({
  agg_id: z.string(),
  locale_code: z.string(),
  country: z.string(),
  category: z.string(),
  aggregator: z.string(),
  events: z.number().int(),
  remaining: z.number().int(),
  sample_pages: z.array(z.string()),
  merchants: z.array(z.string()),
  status: ExtAggStatusSchema,
  check_exists: z.boolean(),
  check_app: z.string(),
  check_summary: z.string(),
  research_task_id: z.string(),
  checked_at: z.string(),
  bridged_at: z.string(),
});
export type PothosExtAggregator = z.infer<typeof PothosExtAggregatorSchema>;
export const PothosExtAggregatorsResSchema = z.object({ ok: z.boolean(), aggregators: z.array(PothosExtAggregatorSchema) });
export const PothosExtAggBridgeReqSchema = z.object({
  agg_id: z.string().min(1),
  research_task_id: z.string().min(1).max(80),
  note: z.string().max(400).default(''),
});
